import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, FileText, AlertTriangle } from 'lucide-react';
import { inventoryService } from '../api/mockRepo';
import { purchaseOrderService } from '../api/mockRepo';
import type { InventoryItem, PurchaseOrder } from '../types';

export default function InventoryOverview() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [itemsData, poData] = await Promise.all([
        inventoryService.getAll(),
        purchaseOrderService.getAll()
      ]);
      
      setItems(itemsData);
      setPurchaseOrders(poData);
    } catch (error) {
      console.error('Failed to load inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const lowStockItems = items.filter(item => item.current_stock <= item.reorder_level);

  const stats = [
    {
      name: 'Total Items',
      value: items.filter(i => i.status === 'active').length.toString(),
      icon: Package,
      color: 'text-blue-600'
    },
    {
      name: 'Low Stock Items',
      value: lowStockItems.length.toString(),
      icon: AlertTriangle,
      color: 'text-red-600'
    },
    {
      name: 'Pending Orders',
      value: purchaseOrders.filter(po => po.status === 'sent' || po.status === 'acknowledged').length.toString(),
      icon: Truck,
      color: 'text-yellow-600'
    },
    {
      name: 'Total Value',
      value: `$${items.reduce((sum, item) => sum + (item.current_stock * item.unit_cost), 0).toFixed(0)}`,
      icon: FileText,
      color: 'text-green-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Inventory & Procurement</h1>
        <p className="text-sm text-neutral-600">Manage inventory items, vendors, and purchase orders</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-lg border p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-neutral-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="text-lg font-medium text-neutral-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Link
          to="/inventory/items"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Inventory Items</h3>
              <p className="text-sm text-neutral-600">Manage inventory items and stock levels</p>
            </div>
          </div>
        </Link>

        <Link
          to="/inventory/vendors"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Truck className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Vendors</h3>
              <p className="text-sm text-neutral-600">Manage supplier information</p>
            </div>
          </div>
        </Link>

        <Link
          to="/inventory/purchase-orders"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Purchase Orders</h3>
              <p className="text-sm text-neutral-600">Create and track purchase orders</p>
            </div>
          </div>
        </Link>
      </div>

      {lowStockItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-lg font-medium text-red-800">Low Stock Alert</h3>
          </div>
          <div className="mt-2">
            <p className="text-sm text-red-700">
              {lowStockItems.length} items are running low on stock and need reordering.
            </p>
            <div className="mt-2 space-y-1">
              {lowStockItems.slice(0, 3).map(item => (
                <div key={item.id} className="text-sm text-red-600">
                  • {item.name} - Current: {item.current_stock}, Reorder Level: {item.reorder_level}
                </div>
              ))}
              {lowStockItems.length > 3 && (
                <div className="text-sm text-red-600">
                  ... and {lowStockItems.length - 3} more items
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
